import type { IObservableArray } from 'mobx';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { Button } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import AddCreativeFlow from '../AddCreativeFlow';
import DuplicateCreativeModal from '../DuplicateCreativeModal';
import type { TChannel, TCreativeType } from '../types';

export interface IAddCreativeButtonProps {
  /** Button theme type */
  type?: 'primary' | 'secondary';
  /** Callback when the creative flow completes */
  onComplete?: (channel: TChannel, creativeType: TCreativeType) => void;
  /** Campaign ID for duplicating creatives */
  campaignId?: string;
  /** Event ID for duplicating creatives */
  eventId?: string;
  /** Observable array of creatives for optimistic updates */
  creatives?: IObservableArray<DisplayCreative>;
  /** Callback to trigger appear animation after adding to list */
  onAppear?: (id: string) => void;
}

/** No modal is open */
type TModalStateNone = { modal: 'none' };

/** AddCreativeFlow modal is open */
type TModalStateAddCreativeFlow = {
  modal: 'addCreativeFlow';
  returnToStep2?: boolean;
  channel?: TChannel;
};

/** DuplicateCreativeModal is open */
type TModalStateDuplicateCreative = {
  modal: 'duplicateCreative';
  channel: TChannel;
};

/** Internal state for managing which modal is currently displayed */
type TModalState = TModalStateNone | TModalStateAddCreativeFlow | TModalStateDuplicateCreative;

function AddCreativeButton({
  type = 'primary',
  onComplete,
  campaignId,
  eventId,
  creatives,
  onAppear,
}: Readonly<IAddCreativeButtonProps>): JSX.Element {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<TModalState>({ modal: 'none' });

  function handleOpenAddCreativeFlow(): void {
    setModalState({ modal: 'addCreativeFlow' });
  }

  function handleCloseAll(): void {
    setModalState({ modal: 'none' });
  }

  function handleAddCreativeFlowComplete(channel: TChannel, creativeType: TCreativeType): void {
    if (creativeType === 'duplicate') {
      setModalState({ modal: 'duplicateCreative', channel });
    } else {
      if (onComplete) {
        onComplete(channel, creativeType);
      }
      setModalState({ modal: 'none' });
    }
  }

  function handleDuplicateModalBack(): void {
    const channel = modalState.modal === 'duplicateCreative' ? modalState.channel : undefined;
    setModalState({
      modal: 'addCreativeFlow',
      returnToStep2: true,
      channel,
    });
  }

  const isAddCreativeFlowOpen = modalState.modal === 'addCreativeFlow';
  const isDuplicateModalOpen = modalState.modal === 'duplicateCreative';

  return (
    <>
      <Button onClick={handleOpenAddCreativeFlow} prefix={getIconForAction('add')} type={type}>
        {t('Add creative')}
      </Button>
      {isAddCreativeFlowOpen && (
        <AddCreativeFlow
          initialChannel={modalState.channel}
          initialStep={modalState.returnToStep2 ? 'creative-type' : 'channel'}
          isOpen={true}
          onClose={handleCloseAll}
          onComplete={handleAddCreativeFlowComplete}
        />
      )}
      {isDuplicateModalOpen && (
        <DuplicateCreativeModal
          campaignId={campaignId}
          channel={modalState.channel}
          creatives={creatives}
          eventId={eventId}
          isOpen={true}
          onAppear={onAppear}
          onBack={handleDuplicateModalBack}
          onClose={handleCloseAll}
        />
      )}
    </>
  );
}

export default AddCreativeButton;
